import { useEffect, useRef, useState, type MutableRefObject } from 'react';

import type { WidgetSizeType } from '../../types';

const JUST_STAMPED_FLOURISH_MS = 800;

const AUTO_REVEAL_MS = 2500;

const REVEAL_START_DELAY_MS = 800;

/** Matches `JUST_STAMPED_CASCADE_STEP_SECONDS`, which staggers the icons themselves. */
const JUST_STAMPED_CASCADE_STEP_MS = 350;

interface UseStampCardRevealParams {
  justStampedIndexes: number[];
  clearJustStamped: () => void;
  isDialogOpen: boolean;
  sizeType: WidgetSizeType;
  current: number;
  isOpeningRef: MutableRefObject<boolean>;
  openWidget: () => void;
  closeWidget: () => void;
}

export function useStampCardReveal({
  justStampedIndexes,
  clearJustStamped,
  isDialogOpen,
  sizeType,
  current,
  isOpeningRef,
  openWidget,
  closeWidget,
}: UseStampCardRevealParams) {
  const [revealedJustStampedIndexes, setRevealedJustStampedIndexes] = useState<number[]>([]);
  const [displayedCurrent, setDisplayedCurrent] = useState(current);

  const pendingRevealRef = useRef(false);
  const previousJustStampedKeyRef = useRef<string>('');
  const autoRevealTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const revealStartTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flourishTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const justStampedKey = justStampedIndexes.join(',');
    const isNewStamp = justStampedIndexes.length > 0 && previousJustStampedKeyRef.current !== justStampedKey;
    previousJustStampedKeyRef.current = justStampedKey;
    if (isNewStamp) pendingRevealRef.current = true;

    if (!pendingRevealRef.current || isDialogOpen) return;
    pendingRevealRef.current = false;

    const isAutoRevealing = sizeType === 'small' && !isOpeningRef.current;

    const reveal = () => {
      // Each extra stamp starts its flourish a beat after the one before it, so the whole cascade
      // has to finish before the flourish is cleared — otherwise a buy-all's later stamps get cut
      // off mid-animation.
      const cascadeMs = Math.max(justStampedIndexes.length - 1, 0) * JUST_STAMPED_CASCADE_STEP_MS;
      const flourishClearMs = (isAutoRevealing ? AUTO_REVEAL_MS : JUST_STAMPED_FLOURISH_MS) + cascadeMs;

      if (isAutoRevealing) {
        openWidget();
        if (autoRevealTimeoutRef.current) clearTimeout(autoRevealTimeoutRef.current);
        autoRevealTimeoutRef.current = setTimeout(() => {
          if (isOpeningRef.current) closeWidget();
        }, AUTO_REVEAL_MS);
      }

      setRevealedJustStampedIndexes(justStampedIndexes);

      if (flourishTimeoutRef.current) clearTimeout(flourishTimeoutRef.current);
      flourishTimeoutRef.current = setTimeout(() => {
        clearJustStamped();
        setRevealedJustStampedIndexes([]);
        setDisplayedCurrent(current);
      }, flourishClearMs);
    };

    if (isAutoRevealing) {
      if (revealStartTimeoutRef.current) clearTimeout(revealStartTimeoutRef.current);
      revealStartTimeoutRef.current = setTimeout(reveal, REVEAL_START_DELAY_MS);
    } else {
      reveal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [justStampedIndexes, isDialogOpen, sizeType]);

  useEffect(() => {
    if (pendingRevealRef.current) return;
    setDisplayedCurrent(current);
  }, [current]);

  useEffect(
    () => () => {
      if (autoRevealTimeoutRef.current) clearTimeout(autoRevealTimeoutRef.current);
      if (revealStartTimeoutRef.current) clearTimeout(revealStartTimeoutRef.current);
      if (flourishTimeoutRef.current) clearTimeout(flourishTimeoutRef.current);
    },
    [],
  );

  return { revealedJustStampedIndexes, displayedCurrent };
}
